import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { 
  Briefcase, Heart, Zap, HelpCircle, TrendingUp, 
  GraduationCap, Users, Home, Stethoscope, Scale, 
  Wallet, Globe, Sparkles
} from "lucide-react";
import Seo from "@/components/Seo";

const CATEGORIES = [
  { 
    id: "career", 
    label: "Career & Exams", 
    icon: Briefcase, 
    color: "bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20",
    iconBg: "bg-blue-500/20",
    description: "Job advice, interview tips, career transitions",
    intent: "Ask Advice"
  },
  { 
    id: "relationships", 
    label: "Relationships", 
    icon: Heart, 
    color: "bg-rose-500/10 text-rose-400 border-rose-500/20 hover:bg-rose-500/20",
    iconBg: "bg-rose-500/20",
    description: "Dating, marriage, family dynamics",
    intent: "Relationship"
  },
  { 
    id: "work", 
    label: "Work & Stress", 
    icon: Zap, 
    color: "bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20",
    iconBg: "bg-amber-500/20",
    description: "Workplace issues, burnout, work-life balance",
    intent: "Work / Stress"
  },
  { 
    id: "life", 
    label: "Life Decisions", 
    icon: HelpCircle, 
    color: "bg-violet-500/10 text-violet-400 border-violet-500/20 hover:bg-violet-500/20",
    iconBg: "bg-violet-500/20",
    description: "Major choices, personal dilemmas",
    intent: "Vent / Confess"
  },
  { 
    id: "education", 
    label: "Education", 
    icon: GraduationCap, 
    color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20 hover:bg-cyan-500/20",
    iconBg: "bg-cyan-500/20",
    description: "Studies, courses, learning paths",
    intent: "Ask Advice"
  },
  { 
    id: "social", 
    label: "Social & Friends", 
    icon: Users, 
    color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20",
    iconBg: "bg-emerald-500/20",
    description: "Friendships, social anxiety, networking",
    intent: "Casual"
  },
  { 
    id: "family", 
    label: "Family", 
    icon: Home, 
    color: "bg-orange-500/10 text-orange-400 border-orange-500/20 hover:bg-orange-500/20",
    iconBg: "bg-orange-500/20",
    description: "Parents, siblings, family expectations",
    intent: "Relationship"
  },
  { 
    id: "health", 
    label: "Health & Wellness", 
    icon: Stethoscope, 
    color: "bg-pink-500/10 text-pink-400 border-pink-500/20 hover:bg-pink-500/20",
    iconBg: "bg-pink-500/20",
    description: "Mental health, fitness, lifestyle",
    intent: "Ask Advice"
  },
  { 
    id: "ethics", 
    label: "Ethics & Morals", 
    icon: Scale, 
    color: "bg-slate-500/10 text-slate-400 border-slate-500/20 hover:bg-slate-500/20",
    iconBg: "bg-slate-500/20",
    description: "Right vs wrong, ethical dilemmas",
    intent: "Vent / Confess"
  },
  { 
    id: "finance", 
    label: "Money & Finance", 
    icon: Wallet, 
    color: "bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20",
    iconBg: "bg-green-500/20",
    description: "Budgeting, investments, financial advice",
    intent: "Ask Advice"
  },
  { 
    id: "culture", 
    label: "Culture & Society", 
    icon: Globe, 
    color: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/20",
    iconBg: "bg-indigo-500/20",
    description: "Cultural questions, traditions, social norms",
    intent: "Casual"
  },
  { 
    id: "trending", 
    label: "Trending", 
    icon: TrendingUp, 
    color: "bg-purple-500/10 text-purple-400 border-purple-500/20 hover:bg-purple-500/20",
    iconBg: "bg-purple-500/20",
    description: "Most discussed topics right now",
    intent: null // Special - sorts by most_answered
  },
];

export default function Categories() {
  const navigate = useNavigate();

  const handleCategoryClick = (category) => {
    if (category.id === "trending") {
      navigate("/browse?sort=most_answered");
    } else {
      navigate(`/browse?intent=${encodeURIComponent(category.intent)}`);
    }
  };

  return (
    <div data-testid="categories-page" className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950">
      <div className="mx-auto max-w-4xl px-4 py-8">
        <Seo title="Categories - AskIncog" description="Browse questions by category" />

        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-slate-100">Browse by Category</h1>
          <p className="mt-2 text-sm text-slate-500">
            Find questions and answers organized by topic
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((cat, i) => (
            <button
              key={cat.id}
              data-testid={`category-${cat.id}`}
              onClick={() => handleCategoryClick(cat)}
              className="text-left animate-fadeInUp"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <Card className={`h-full border p-5 transition-all backdrop-blur-sm ${cat.color}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${cat.iconBg}`}>
                    <cat.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-slate-100">{cat.label}</h3>
                    <p className="mt-1 text-xs text-slate-400">{cat.description}</p>
                  </div>
                </div>
              </Card>
            </button>
          ))}
        </div>

        {/* Featured / Random Prompt */}
        <div className="mt-10">
          <Card className="border-indigo-500/20 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 p-6 text-center backdrop-blur-sm">
            <Sparkles className="mx-auto h-6 w-6 text-indigo-400" />
            <h3 className="mt-3 font-medium text-slate-100">Can't find your category?</h3>
            <p className="mt-1 text-sm text-slate-400">
              Just ask your question - our AI will help categorize it for you.
            </p>
            <button
              onClick={() => navigate("/")}
              className="mt-4 inline-flex h-10 items-center rounded-lg bg-indigo-600 px-5 text-sm font-medium text-white hover:bg-indigo-500 transition-colors"
            >
              Ask a Question
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
}
